import type { Report, ReportDefinition, ReportRange } from '@ofmos/shared/report';
import { hasFlag } from '@ofmos/shared/roles';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  useCreateReport,
  useFields,
  useObjects,
  useReportRun,
  useReports,
  useUpdateReport,
} from '../../data/hooks';
import {
  Button,
  Dialog,
  DropdownItem,
  DropdownMenu,
  EmptyState,
  IconButton,
  SegmentedControl,
} from '../../ui';
import { useSession } from '../auth/useSession';
import { BackButton } from '../nav/BackLink';
import { useDetailMaxWidth } from '../records/ListDetailPane';
import { PageHeader } from '../shell/PageHeader';
import { PaneHost } from '../shell/PaneHost';
import { tokenPx, useRememberedWidth } from './paneWidth';
import { ReportDetail } from './ReportDetail';
import { type ReportDraft, ReportInspector } from './ReportInspector';
import { ReportsOverview } from './ReportsOverview';
import { reportChartData } from './reportSeries';
import { groupReports, RANGE_SEGMENTS, type ReportView, VIEW_SEGMENTS } from './reportsModel';

/**
 * THE REPORTS SCREEN: AN OVERVIEW, AND A REPORT PUSHED OVER IT (wave 5 round 3, C4-2).
 *
 * Both routes render this component. `/reports` is the overview - every report as a card
 * with its chart, grouped by object - and `/reports/:reportId` is that report with the
 * editor optionally beside it. It stopped being a SPLIT VIEW in this round: a split view
 * earns its list when the list is how you navigate a long detail, and here the detail is
 * eight pictures that fit on one screen, so the list was two thirds of a window spent on an
 * index to content that could simply be shown (the owner: "Warum sollte das so aussehen
 * statt eine Report-Seite, die alle Charts und Tables aufzeigt?").
 *
 * THE EDITOR STILL APPEARS AS A COLUMN AT 1024px, which is the rule the record list already
 * follows (`.list-detail` in index.css, rulebook PH3: "no two-pane layout below 768px", and
 * this product draws the line at 1024 where the detail still has room). Below it the editor
 * is a sheet, which is the case `ui/useIsDesktop` used to be read for here and is now read
 * once, as `splitView`.
 */
const SPLIT_QUERY = '(min-width: 64rem)';
const INSPECTOR_WIDTH_KEY = 'ofmos.reports.inspectorWidth';

function useSplitView(): boolean {
  const [split, setSplit] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(SPLIT_QUERY).matches === true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(SPLIT_QUERY);
    const onChange = () => setSplit(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);
  return split;
}

/**
 * OFFLINE IS THE BROWSER'S OWN ANSWER, and it is read here rather than inferred from a
 * failed request: a request that failed may have failed for one reason, and the notice
 * `components/MAP.md` describes is about the connection rather than about this report.
 */
function useOffline(): boolean {
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  useEffect(() => {
    const update = () => setOffline(navigator.onLine === false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return offline;
}

export function ReportsScreen() {
  const { reportId = '' } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const splitView = useSplitView();
  const offline = useOffline();

  const reports = useReports();
  const objects = useObjects();
  const createReport = useCreateReport();
  const updateReport = useUpdateReport();

  const [range, setRange] = useState<ReportRange | null>(null);
  const [view, setView] = useState<ReportView>('chart');
  const [editorOpen, setEditorOpen] = useState(false);
  /** A report being built: it has no id until the schema accepts a whole definition. */
  const [creating, setCreating] = useState(false);
  /**
   * THE REPORT THAT WAS JUST WRITTEN, HELD UNTIL THE LIST HAS READ IT BACK (found by the
   * third full gate of this round, which lost a typed name under load).
   *
   * `create` answers with the row, the screen navigates to its route, and the LIST is what
   * `selected` is resolved from - so between the navigation and the list's refetch there
   * was a window where the URL named a report and `selected` was still null. In that window
   * the inspector's key was still "new", `onRename` returned early on the null and the name
   * the person had typed was dropped without a word. A write must not depend on a read
   * having landed, so the created row stands in for the list until the list carries it.
   */
  const [justCreated, setJustCreated] = useState<Report | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);

  const [inspectorWidth, setInspectorWidth] = useRememberedWidth(INSPECTOR_WIDTH_KEY);
  /**
   * THE THREE NUMBERS PER PANE (Apple's `min`, `ideal`, `max`), AND NOT ONE OF THEM IS NEW.
   *
   * ONE SIDE PANE SINCE WAVE 5 ROUND 3, because the master list is gone (C4-2): what is left
   * is the report and the editor beside it. The inspector starts at `--sidebar-width-max`,
   * whose own comment is the reason it fits here: "wide enough for a long object name,
   * narrow enough that the content pane keeps most of the window"; the same token is what
   * the report keeps for itself when the editor is dragged wide.
   *
   * THE KIT'S 460 FORM IS DELIBERATELY NOT THE INSPECTOR'S WIDTH, because this product has
   * already tried it: wave 3's S-C1 took the 460 column OFF the settings pane with the note
   * that "Apple pairs that 460 form with a 500pt window ... so the 460 column left two
   * thirds of a 1440 window empty" (tokens.css, `--settings-pane-inset`). Driven here at
   * 1440 before it was changed: a 460 inspector left the chart 313px of the window.
   *
   * The ceiling is half the SPLIT, a proportion rather than a measure and therefore no
   * token at all: past it the thing the screen is for stops being the biggest thing on it.
   */
  const [split, setSplitElement] = useState<HTMLElement | null>(null);
  const [limits] = useState(() => ({
    /** What the inspector may never eat: the report itself keeps at least this much. */
    detailFloor: tokenPx('--sidebar-width-max'),
    inspector: tokenPx('--sidebar-width-max'),
  }));
  const paneMax = useDetailMaxWidth(split, limits.detailFloor);

  const selected: Report | null =
    reportId === ''
      ? null
      : (reports.data?.find((report) => report.id === reportId) ??
        (justCreated?.id === reportId ? justCreated : null));
  const definition = selected?.definition ?? null;
  const objectId = definition?.objectId ?? '';
  const fields = useFields(objectId, objectId !== '');
  const effectiveRange = range ?? definition?.range ?? 'all_time';
  const run = useReportRun(reportId, range ?? undefined);
  const object = objects.data?.find((entry) => entry.id === objectId);

  /** A different report is a different question: the range and the view start over. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the reset is keyed on the report, not on the state it resets
  useEffect(() => {
    setRange(null);
    setView('chart');
    setWriteError(null);
  }, [reportId]);

  const permissions = session.data?.permissions;
  const canManage = permissions !== undefined && hasFlag(permissions, 'REPORTS_MANAGE');
  const groups = groupReports(reports.data ?? [], objects.data ?? []);
  const data =
    selected === null || run.data === undefined || fields.data === undefined
      ? { series: [], points: [], total: null }
      : reportChartData({
          definition: selected.definition,
          result: run.data.result,
          fields: fields.data,
          today: new Date(),
        });

  const message = (error: unknown): string =>
    error instanceof Error ? error.message : 'This report could not be saved. Try again.';

  /**
   * THE REPORT SAVES AS IT CHANGES (section 5). A report that exists is patched; a report
   * being built is created the moment the schema accepts it, and the screen then points at
   * it - which is what makes "no Save button" a construction rather than a missing control.
   */
  const commit = (draft: ReportDraft, accepted: ReportDefinition) => {
    setWriteError(null);
    const input = {
      name: draft.name,
      description: draft.description === '' ? null : draft.description,
      definition: accepted,
    };
    if (selected === null) {
      createReport.mutate(input, {
        onSuccess: (created) => {
          setCreating(false);
          setJustCreated(created);
          void navigate(`/reports/${created.id}`);
        },
        onError: (error) => setWriteError(message(error)),
      });
      return;
    }
    updateReport.mutate(
      { reportId: selected.id, patch: input },
      { onError: (error) => setWriteError(message(error)) },
    );
  };

  const rename = (name: string, description: string) => {
    if (selected === null || name.trim() === '') return;
    if (name === selected.name && description === (selected.description ?? '')) return;
    setWriteError(null);
    updateReport.mutate(
      {
        reportId: selected.id,
        patch: { name, description: description === '' ? null : description },
      },
      { onError: (error) => setWriteError(message(error)) },
    );
  };

  const startNew = () => {
    setCreating(true);
    setEditorOpen(true);
    if (reportId !== '') void navigate('/reports');
  };

  const duplicate = () => {
    if (selected === null) return;
    setWriteError(null);
    createReport.mutate(
      {
        name: `${selected.name} copy`,
        description: selected.description,
        definition: selected.definition,
      },
      {
        onSuccess: (created) => void navigate(`/reports/${created.id}`),
        onError: (error) => setWriteError(message(error)),
      },
    );
  };

  /**
   * THE TOOLBAR (section 2 item 2). The centre area carries the two controls that act on
   * the WHOLE view - Chart or Table, then the stretch of time - with the kit's own toolbar
   * gap between them; the trailing edge carries the editor toggle, New Report and the More
   * menu, all of them symbol-first items with no tint (triage C2-2).
   */
  const viewControl = (
    <SegmentedControl
      size="sm"
      value={view}
      onValueChange={setView}
      segments={VIEW_SEGMENTS}
      ariaLabel="How this report is shown"
    />
  );
  const rangeControl = (
    <SegmentedControl
      size="sm"
      value={effectiveRange}
      onValueChange={setRange}
      segments={RANGE_SEGMENTS}
      ariaLabel="The stretch of time this report covers"
    />
  );

  /**
   * THE CENTRE AREA IS THE DESKTOP'S. A unified toolbar has a centre and a phone's
   * navigation bar has none, so below the split both of these controls come down into the
   * content: Chart and Table over the drawing, the range under it (section 3 item 2 as
   * corrected on 2026-09-16). The earlier reading put Chart and Table in the bar's TRAILING
   * slot, which is what squeezed the window title to zero width at 375 (triage E-W5-1).
   */
  const centre =
    selected === null || !splitView ? undefined : (
      <div className="flex items-center gap-(--shell-toolbar-gap)">
        {viewControl}
        {rangeControl}
      </div>
    );

  /**
   * THE PUSHED REPORT, which is the one place the bar is a phone's NAVIGATION bar: a back
   * control, the title, and what is left of the trailing slot. Its budget is what the title
   * is measured against, so this is a named condition rather than two inline `!splitView`s.
   */
  const pushed = !splitView && selected !== null;

  const actions = canManage ? (
    <>
      {(selected !== null || creating) && (
        <IconButton
          icon="pencil"
          label={editorOpen ? 'Hide editor' : 'Show editor'}
          aria-expanded={editorOpen}
          onClick={() => setEditorOpen((open) => !open)}
        />
      )}
      {/*
        NEW REPORT KEEPS ITS OWN ITEM EVERYWHERE EXCEPT ON THE PUSHED REPORT, where it is
        the third symbol in a bar that owes a reader the report's name first (HIG Toolbars:
        "keep the title under 15 characters long so you leave enough room for other
        controls"). On that one screen it is a menu item instead; it stays the toolbar item
        on the list screen and at every width at and above the split.
      */}
      {!pushed && <IconButton icon="plus" label="New report…" onClick={startNew} />}
      {selected !== null && (
        /*
          THE MORE MENU CARRIES WHAT IT CAN ACTUALLY DO. Section 2 item 2 lists four items
          and three of them have no mechanism on this tree: Pin to Home needs the Owner
          preset's group model in `app/dashboard`, which this slice may not touch; Export
          has no report export on the client or the server (`ExportAction` exports a record
          QUERY, not a grouped result); and Delete has no confirmation flow wired here.
          The rebuilt sheet primitive exists, but this round does not add an unrelated
          destructive action. Three dead controls would be worse than a short menu,
          so each is an OPEN caption on the states page and a ledger entry. */
        <DropdownMenu align="end" trigger={<IconButton icon="more-horizontal" label="More" />}>
          {pushed && (
            <DropdownItem icon="plus" onSelect={startNew}>
              New report…
            </DropdownItem>
          )}
          <DropdownItem icon="plus" onSelect={duplicate}>
            Duplicate
          </DropdownItem>
        </DropdownMenu>
      )}
    </>
  ) : undefined;

  /**
   * THE SCREEN IS THE OVERVIEW UNTIL A REPORT IS NAMED, and the condition is the ROUTE
   * rather than the resolved report: `/reports/:id` reached by URL has `selected === null`
   * for as long as the list takes to read, and a screen that decided on `selected` would
   * paint the whole overview into that gap and then replace it.
   */
  const opened = reportId !== '' || creating;

  const detail =
    selected !== null ? (
      <ReportDetail
        report={selected}
        object={object}
        data={data}
        view={view}
        range={effectiveRange}
        loading={run.isPending || fields.isPending}
        refetching={run.isFetching && !run.isPending}
        error={run.error}
        offline={offline}
        onRetry={() => void run.refetch()}
        {...(splitView ? {} : { rangeControl, viewControl })}
      />
    ) : creating ? (
      <section className="reports-detail" aria-label="New report">
        {/* The same pane in another state, so it keeps the same scrollport (G5, C6-5); the
            body stays INSIDE the host for the reason `ReportDetail` records - its own
            `padding-block` would otherwise beat the host's bar-height inset. */}
        <PaneHost host="report-new">
          <div className="reports-detail-body">
            <EmptyState
              icon="activity"
              title="New report"
              description="Answer the questions in the editor and the chart appears here."
            />
          </div>
        </PaneHost>
      </section>
    ) : (
      /* A named report that the list has not carried yet: the pane, and nothing in it. */
      <section className="reports-detail" aria-label="Reports" />
    );

  const inspector =
    canManage && (selected !== null || creating) ? (
      <ReportInspector
        key={selected?.id ?? 'new'}
        report={selected}
        split={splitView}
        width={inspectorWidth ?? limits.inspector}
        min={limits.inspector}
        max={Math.max(paneMax, limits.inspector)}
        onWidth={setInspectorWidth}
        onCommit={commit}
        onRename={rename}
        writeError={writeError}
      />
    ) : null;

  /**
   * THE WAY BACK EXISTS AT EVERY WIDTH NOW (C4-2). It used to be the phone's alone, because
   * on the Mac the list pane beside the report WAS the way back; with the list gone, a
   * report opened from a card is a pushed screen at 1440 as well, and a window that pushes
   * owes its reader the control that comes back (hig:
   * https://developer.apple.com/design/human-interface-guidelines/toolbars).
   */
  const leading = selected === null ? undefined : <BackButton to="/reports" label="Reports" />;

  return (
    <div className="reports-screen">
      <PageHeader
        title={selected?.name ?? 'Reports'}
        {...(centre === undefined ? {} : { centre })}
        {...(actions === undefined ? {} : { actions })}
        {...(leading === undefined ? {} : { leading })}
      />

      {opened ? (
        <div className="reports-split" ref={setSplitElement}>
          {detail}
          {splitView && editorOpen ? inspector : null}
        </div>
      ) : (
        /*
          THE OVERVIEW IS THE SAME COMPONENT AT BOTH WIDTHS (C4-2), which is the one place
          this screen stopped needing two: the phone's list and the desktop's list pane were
          two components because one was a source list in a pane and the other an inset
          grouped list, and a grid of cards is neither - it is the content, and it reflows
          from two columns to one.
        */
        /*
          THE GRID OF CARDS IS THE SCREEN'S SCROLLPORT (wave 5 round 5, G5; owner decision
          C6-5). It used to scroll the WINDOW, so the cards passed under the bar only while
          the document happened to be taller than the window; the host makes the pane under
          the bar the scroller, which is where the kit puts it (`doc:`
          docs/decisions/ui-foundation-w5.md, A0). "New Report" stays in the window toolbar
          - it is a window-level action - and nothing about the cards changes.
        */
        <PaneHost host="reports-overview">
          <ReportsOverview
            groups={groups}
            loading={reports.isPending}
            canManage={canManage}
            onNew={startNew}
          />
        </PaneHost>
      )}
      {!splitView && (
        /* The existing rebuilt Dialog is the phone sheet (ui/Dialog, wave 3).
           Its surface and overlay inherit the rise/fade families from motion.css. */
        <Dialog
          open={editorOpen && inspector !== null}
          onOpenChange={setEditorOpen}
          title="Report editor"
          variant="sheet"
          footer={
            <div className="flex justify-end">
              <Button variant="push" onClick={() => setEditorOpen(false)}>
                Done
              </Button>
            </div>
          }
        >
          {inspector}
        </Dialog>
      )}
    </div>
  );
}
